import * as assert from 'assert-plus';
import { useState, useEffect, useRef } from 'react';
import { logError } from 'src/utils/Error';
import IError from 'src/models/IError';
import IValidationError from 'src/models/IValidationError';
import { createNewWorkspaceURI } from 'src/contexts/api';
import { MeltanoVersion } from 'src/models/IWorkspace';

//
// Configures the add / edit workspace form
//

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
const useWorkspaceForm = (api: any,
    savedCallback?: (updatedWorkspace: any) => void,
    deletedCallback?: (deletedWorkspace: any) => void,
) => {

    const isMounted = useRef(false)
    useEffect(() => {
      isMounted.current = true;
      return () => { isMounted.current = false }
    }, []);

    const [workspace, setWorkspace] = useState({
      id: null,
      name: '',
      domains: [],
      imageUrl: '',
      defaultEnvironment: undefined,
      repositoryUrl: undefined,
      repositoryBranch: undefined,
      repositoryDirectory: undefined,
      sshPrivateKey: undefined,
      githubInstallationId: undefined,
      meltanoVersion: null as MeltanoVersion | null,
      appProperties: {},
      _links: {}
    });

    const [errors, setErrors] = useState<IError[]>([]);
    const [validationErrors, setValidationErrors] = useState<IValidationError[]>([]);

    const [name, setName] = useState<string>('');
    const [domains, setDomains] = useState<string[]>([]);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [defaultEnvironment, setDefaultEnvironment] = useState<string>()
    const [meltanoVersion, setMeltanoVersion] = useState<MeltanoVersion | null>(null)
    const [repositoryUrl, setRepositoryUrl] = useState<string>()
    const [repositoryBranch,  setRepositoryBranch] = useState<string>()
    const [repositoryDirectory, setRepositoryDirectory] = useState<string>()
    const [sshPrivateKey,  setSshPrivateKey] = useState<string>()
    const [githubInstallationId,  setGithubInstallationId] = useState<string>()
    const [appProperties, setAppProperties] = useState({});
    const [settingValues, setSettingValues] = useState<{ [name: string]: string | null }>({});

    const [isNew, setIsNew] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeploying, setDeploying] = useState(false);
    const [deployError, setDeployError] = useState<string | null>(null);
    const [isShowDeleteConfirmation, setIsShowDeleteConfirmation] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [disabledElements, setDisabledElements] = useState({
      isSubmitDisabled: false,
      isDeleteDisabled: false,
    });

    const defaultBranch = "main"
    const urlMatch = repositoryUrl?.match(/^(git@|https:\/\/)(?<host>\S+?)(:|\/)\S+/)
    const repostoryUrlHost = urlMatch?.groups?.["host"]
    const isRemoteGitRepo = !!(repositoryUrl?.endsWith(".git") || (repostoryUrlHost && ["github.com", "gitlab.com"].includes(repostoryUrlHost)))

    useEffect(() => {
      setIsNew(workspace && workspace._links && workspace._links['update delete workspace'] == null)
      // when editing, we have an existing workspace
      if (workspace) {
          workspace.name && setName(workspace.name);
          workspace.domains.length && setDomains(workspace.domains);
          workspace.imageUrl && setImageUrl(workspace.imageUrl);
          setDefaultEnvironment(workspace.defaultEnvironment);
          setMeltanoVersion(workspace.meltanoVersion ?? null);
          setRepositoryUrl(workspace.repositoryUrl);
          setRepositoryBranch(workspace.repositoryBranch);
          setRepositoryDirectory(workspace.repositoryDirectory);
          setSshPrivateKey(workspace.sshPrivateKey);
          setGithubInstallationId(workspace.githubInstallationId)
          setAppProperties(workspace.appProperties);
      }
    }, [workspace] )

    useEffect(() => {
        setDisabledElements({
            isSubmitDisabled: name == null || !name.length || isDeleting || isDeploying || isSaving,
            isDeleteDisabled: isDeleting || isDeploying || isSaving,
        })
    }, [name, isDeleting, isDeploying, isSaving] )

    async function initialiseWorkspace() {
        await api
          .post(createNewWorkspaceURI())
          .then((resp: any) => {
              isMounted.current && setWorkspace(resp.data)
          })
          .catch((e: any) => {
              isMounted.current && logError(e)
          })
    }

    function handleSubmit(event: any) {
        event.preventDefault();
        assert.object(workspace, 'workspace must be loaded before submit');
        assert.object(workspace._links, 'workspace links required');
        // create or update link
        const createLink = workspace._links['create workspace'];
        const updateLink = workspace._links['update delete workspace'];
        const link = updateLink ? updateLink : createLink;
        assert.object(link, 'workspace create or update link required');

        setIsSaving(true)

        // reset errors
        setErrors([]);
        // reset validation errors
        setValidationErrors([]);
        // define workspace to be created / updated
        const workspaceResource = {
            name: name,
            domains: domains,
            defaultEnvironment: defaultEnvironment || null,
            meltanoVersion: meltanoVersion || null,
            repositoryUrl: repositoryUrl || undefined,
            repositoryBranch: repositoryUrl && repositoryBranch || (isRemoteGitRepo && defaultBranch) || undefined,
            repositoryDirectory: repositoryUrl ? repositoryDirectory || undefined : undefined,
            sshPrivateKey: sshPrivateKey || undefined,
            githubInstallationId: githubInstallationId || undefined,
            ...(imageUrl && { imageUrl: imageUrl }),
            appProperties: appProperties,
            settings: Object.entries(settingValues).map(([name, value]) => ({ name, value }))
        }
        api.put(link.href, workspaceResource, { errorHandler: false })
            .then((resp: any) => {
                if (resp && (resp.status === 200 || resp.status === 201)) {
                    setIsSaving(false)
                    setWorkspace(resp.data)
                    savedCallback && savedCallback(resp.data)
                }
            })
            .catch((e: any) => {
                if (e.response && e.response.status === 400) {
                    const errors = e.response.data.errors
                    setValidationErrors(errors)
                } else {
                    if (e.response && e.response.data && e.response.data.message) {
                        const error = e.response.data
                        setErrors([error])
                    } else {
                        setErrors([{ message: "An unexpected error occurred" }])
                    }
                    logError(e);
                }
                setIsSaving(false)
            })
    }

    const handleDeploy = () => {
        setDeploying(true)

        const createDeploymentLink = workspace._links['create deployment'];
        assert.object(createDeploymentLink, '`create deployment` link not present')

        api.post(createDeploymentLink.href, null, { errorHandler: false })
            .catch((e: any) => {
                setDeployError(e.response?.data?.detail || e.response?.data?.message || 'Unable to deploy the workspace')
                logError(e)
            })
            .finally(() => setDeploying(false))
    }

    async function handleDelete() {
        assert.object(workspace, 'workspace must be loaded before delete');
        assert.object(workspace._links, 'workspace links required');

        setIsDeleting(true)

        // reset errors
        setErrors([]);
        // reset validation errors
        setValidationErrors([]);
        // delete link
        const deleteLink = workspace._links['update delete workspace'];
        api.delete(deleteLink.href, { errorHandler: false })
            .then(() => {
                setIsDeleting(false)
                deletedCallback && deletedCallback(workspace)
            })
            .catch((e: any) => {
                if (e.response && e.response.status === 400) {
                    const errors = e.response.data.errors
                    setValidationErrors(errors)
                } else {
                    if (e.response && e.response.data && e.response.data.message) {
                        const error = e.response.data
                        setErrors([error])
                    } else {
                        setErrors([{ message: "An unexpected error occurred" }])
                    }
                    logError(e);
                }
                setIsDeleting(false)
            })
            .finally(() => {
              setIsShowDeleteConfirmation(false)
            });
    }

    function handleRegenerateDeploymentSecret(event: any) {
        event.preventDefault();
        assert.object(workspace, 'workspace must be loaded before submit');
        assert.object(workspace._links, 'workspace links required');
        // create or update link
        const createLink = workspace._links['create workspace'];
        const updateLink = workspace._links['update delete workspace'];
        const link = updateLink ? updateLink : createLink;
        assert.object(link, 'workspace create or update link required');

        setIsSaving(true)

        // reset errors
        setErrors([]);
        // reset validation errors
        setValidationErrors([]);
        // define workspace to be created / updated
        const workspaceResource = {
            name: name,
            domains: domains,
            deploymentSecret: '',
            ...(imageUrl && { imageUrl: imageUrl }),
        }
        api.put(link.href, workspaceResource, { errorHandler: false })
            .then((resp: any) => {
                if (resp && (resp.status === 200 || resp.status === 201)) {
                    setIsSaving(false)
                    setWorkspace(resp.data)
                    savedCallback && savedCallback(resp.data)
                }
            })
            .catch((e: any) => {
                if (e.response && e.response.status === 400) {
                    const errors = e.response.data.errors
                    setValidationErrors(errors)
                } else {
                    if (e.response && e.response.data && e.response.data.message) {
                        const error = e.response.data
                        setErrors([error])
                    } else {
                        setErrors([{ message: "An unexpected error occurred" }])
                    }
                    logError(e);
                }
                setIsSaving(false)
            })
    }

    function onChangeName(event: any) {
      setName(event.target.value);
    }

    function onChangeImageUrl(imageUrl: any) {
        setImageUrl(imageUrl)
    }

    const onSetRepositoryUrl = (event: any) => setRepositoryUrl(event.target.value)
    const onSetRepositoryBranch = (event: any) => setRepositoryBranch(event.target.value)
    const onSetRepositoryDirectory = (event: any) => setRepositoryDirectory(event.target.value)
    const onSetSshPrivateKey = (event: any) => setSshPrivateKey(event.target.value)
    const onSetGithubInstallationId = (event: any) => setGithubInstallationId(event.target.value)

    return {
        api,
        disabledElements,
        initialiseWorkspace,
        workspace,
        setWorkspace,
        name,
        onChangeName,
        domains,
        setDomains,
        imageUrl,
        onChangeImageUrl,
        handleSubmit,
        handleDeploy,
        handleDelete,
        errors,
        validationErrors,
        isNew,
        setIsNew,
        isSaving,
        isDeploying,
        deployError,
        setDeployError,
        isShowDeleteConfirmation,
        setIsShowDeleteConfirmation,
        isDeleting,
        handleRegenerateDeploymentSecret,
        onSetRepositoryUrl,
        repositoryUrl,
        onSetRepositoryBranch,
        repositoryBranch,
        onSetRepositoryDirectory,
        repositoryDirectory,
        onSetSshPrivateKey,
        sshPrivateKey,
        onSetGithubInstallationId,
        githubInstallationId,
        defaultEnvironment,
        setDefaultEnvironment,
        meltanoVersion,
        setMeltanoVersion,
        defaultBranch,
        isRemoteGitRepo,
        repostoryUrlHost,
        settingValues,
        setSettingValues,
    };
};

export default useWorkspaceForm
